<template>
  <div class="icon-wrapper">
    <span v-bind="$attrs" @click="togglePicker" class="icon-picker-trigger">
      <i class="vue3-icon-picker icon-3d" :class="[modelValue]"></i>
    </span>
  </div>
  <div class="aim-modal aim-open" v-if="isVisible">
    <div class="aim-modal--content">
      <div class="aim-modal--header">
        <div class="aim-modal--header-logo-area">
          <span class="aim-modal--header-logo-title">
            {{ label }}
          </span>
        </div>
        <div class="aim-modal--header-close-btn" @click="closePicker">
          <i class="fas fa-times" title="Close"></i>
        </div>
      </div>
      <div class="aim-modal--body">
        <div class="aim-modal--sidebar">
          <div class="aim-modal--sidebar-tabs">
            <div
              class="aim-modal--sidebar-tab-item"
              data-library-id="all"
              v-for="tab in tabs"
              :key="tab.id"
              :class="{ 'aesthetic-active': isActiveTab(tab.id) }"
              @click="setActiveTab(tab)">
              <i :class="tab.icon"></i>
              <span>{{ tab.title }}</span>
            </div>
          </div>
          <div class="aim-sidebar-preview">
            <div class="aim-icon-item ">
              <div class="aim-icon-item-inner">
                <i :class="activeGlyph"></i>
                <div class="aim-icon-item-name">
                  {{ activeGlyph }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="aim-modal--icon-preview-wrap">
          <div class="aim-modal--icon-search">
            <input v-model="filterText" placeholder="Filter by name..." />
            <i class="fas fa-search"> </i>
          </div>
          <div class="aim-modal--icon-preview-inner">
            <div class="aim-modal--icon-preview">
              <div
                class="aim-icon-item"
                v-for="glyph in glyphs"
                :key="glyph"
                :class="{ 'aesthetic-selected': isActiveGlyph(glyph) }"
                @click="setActiveGlyph(glyph)">
                <div class="aim-icon-item-inner">
                  <i :class="glyph"></i>
                  <div class="aim-icon-item-name">
                    {{ getGlyphName(glyph) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Pagination Controls -->
          <div class="aim-modal--pagination" v-if="totalPages > 1">
            <button
              class="aim-pagination-button"
              @click="currentPage = Math.max(1, currentPage - 1)"
              :disabled="currentPage === 1">
              Previous
            </button>
            <span class="aim-pagination-info">
              Page {{ currentPage }} of {{ totalPages }}
            </span>
            <button
              class="aim-pagination-button"
              @click="currentPage = Math.min(totalPages, currentPage + 1)"
              :disabled="currentPage === totalPages">
              Next
            </button>
          </div>
        </div>
      </div>
      <div class="aim-modal--footer">
        <button class="aim-insert-icon-button" @click="insert">Insert</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import fontLibrary from './data/fonts'

export default {
  inheritAttrs:false,
  props: {
    label: {
      type: String,
      default: 'Pick Your Icon'
    },
    modelValue: {
      type: String,
      default: 'fas fa-arrow-pointer'
    }
  },
  setup (props, context) {
    const filterText = ref('')
    const activeGlyph = ref(props.modelValue || 'fas fa-arrow-pointer') // Default value when null
    const isVisible = ref(false)
    const currentPage = ref(1)
    const itemsPerPage = ref(100)
    const isLoading = ref(false)

    const tabs = [
      {
        id: 'all',
        title: 'All Icons',
        icon: 'fas fa-star-of-life',
        link: 'all'
      },
      {
        id: 'fas',
        title: 'Font Awesome Pro Solid',
        icon: 'fas fa-flag',
        link: fontLibrary.fontAwesome.variants.solid
      },
      {
        id: 'far',
        title: 'Font Awesome Pro Regular',
        icon: 'far fa-flag',
        link: fontLibrary.fontAwesome.variants.regular
      },
      {
        id: 'fal',
        title: 'Font Awesome Pro Light',
        icon: 'fal fa-flag',
        link: fontLibrary.fontAwesome.variants.light
      },
      {
        id: 'fat',
        title: 'Font Awesome Pro Thin',
        icon: 'fat fa-flag',
        link: fontLibrary.fontAwesome.variants.thin
      },
      {
        id: 'fad',
        title: 'Font Awesome Pro Duotone',
        icon: 'fad fa-flag',
        link: fontLibrary.fontAwesome.variants.duotone
      },
      {
        id: 'fab',
        title: 'Font Awesome Brands',
        icon: 'fab fa-font-awesome-flag',
        link: fontLibrary.fontAwesome.variants.brands
      }
    ]

    const activeTab = ref(tabs[0])

    const allGlyphs = computed(() => {
      return tabs.reduce((acc, tab) => {
        if (tab.id !== 'all' && tab.link && tab.link.icons) {
          return acc.concat(tab.link.icons)
        }
        return acc
      }, [])
    })

    // Filtered glyphs without pagination
    const filteredGlyphs = computed(() => {
      let _glyphs = []
      if (activeTab.value.id !== 'all') {
        _glyphs = activeTab.value.link?.icons || []
      } else {
        _glyphs = allGlyphs.value || []
      }

      if (filterText.value !== '') {
        const _filterText = filterText.value.toLowerCase()
        _glyphs = _glyphs.filter(item => {
          if (typeof item !== 'string') return false
          const match = item.match(/fa-([^-\s]+(?:-[^-\s]+)*)/);
          return match ? match[1].toLowerCase().includes(_filterText) : false
        })
      }
      return _glyphs
    })

    // Total pages calculation
    const totalPages = computed(() => {
      return Math.ceil(filteredGlyphs.value.length / itemsPerPage.value)
    })

    // Paginated and filtered glyphs
    const glyphs = computed(() => {
      const startIndex = (currentPage.value - 1) * itemsPerPage.value
      const endIndex = startIndex + itemsPerPage.value
      return filteredGlyphs.value.slice(startIndex, endIndex)
    })

    const setActiveGlyph = glyph => {
      activeGlyph.value = glyph
    }

    const isActiveGlyph = glyph => {
      return activeGlyph.value == glyph
    }

    const isActiveTab = tab => {
      return tab == activeTab.value.id
    }

    const setActiveTab = tab => {
      activeTab.value = tab
      currentPage.value = 1 // Reset to first page when changing tabs
      filterText.value = '' // Clear search when changing tabs
    }

    const getGlyphName = glyph => {
      if (typeof glyph !== 'string') return ''
      const match = glyph.match(/fa-([^-\s]+(?:-[^-\s]+)*)/);
      return match ? match[1].replace(/-/g, ' ') : ''
    }

    const insert = () => {
      context.emit('update:modelValue', activeGlyph.value)
      isVisible.value = false
    }

    const togglePicker = () => {
      isVisible.value = !isVisible.value
    }

    const closePicker = () => {
      isVisible.value = false
    }

    const animations = {
      "fas fa-star": "spin",
      "fas fa-heart": "pulse",
      "fas fa-arrow": "bounce",
      "fas fa-circle": "rotate",
    };

    const animationClass = computed(() => {
      return animations[props.modelValue] || "default-animation";
    });

    return {
      fontLibrary,
      tabs,
      isActiveTab,
      setActiveTab,
      glyphs,
      activeGlyph,
      getGlyphName,
      filterText,
      setActiveGlyph,
      isActiveGlyph,
      insert,
      isVisible,
      togglePicker,
      closePicker,
      currentPage,
      totalPages,
      isLoading,
      animationClass
    }
  }
}
</script>

<style scoped>
@import '../assets/css/style.css';

.icon-wrapper {
  perspective: 1000px;
  display: inline-block;
}

.icon-3d {
  display: inline-block;
  font-size: 1.5em;
  color: #3b82f6;
  transform: translateY(0);
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px rgba(50, 50, 93, 0.11),
              0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 8px;
  border-radius: 8px;
  background: white;
}

.icon-3d:hover {
  color: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 7px 14px rgba(50, 50, 93, 0.1),
              0 3px 6px rgba(0, 0, 0, 0.08);
}
@keyframes spin3d {
  0% {
    transform: rotate3d(0, 1, 0, 0deg);
  }
  100% {
    transform: rotate3d(0, 1, 0, 360deg);
  }
}

@keyframes pulse3d {
  0%, 100% {
    transform: scale3d(1, 1, 1);
  }
  50% {
    transform: scale3d(1.2, 1.2, 1.2);
  }
}

@keyframes bounce3d {
  0%, 100% {
    transform: translateY(0) rotate3d(1, 1, 1, 0deg);
  }
  50% {
    transform: translateY(-10px) rotate3d(1, 1, 1, 10deg);
  }
}

@keyframes rotate3d {
  0% {
    transform: rotate3d(1, 1, 1, 0deg);
  }
  100% {
    transform: rotate3d(1, 1, 1, 360deg);
  }
}
.spin {
  animation: spin3d 2s infinite linear;
}

.pulse {
  animation: pulse3d 1.5s infinite ease-in-out;
}

.bounce {
  animation: bounce3d 1s infinite ease-in-out;
}

.rotate {
  animation: rotate3d 2s infinite linear;
}

.default-animation {
  animation: pulse3d 1.5s infinite ease-in-out;
}

.aim-modal--pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  gap: 1rem;
  border-top: 1px solid #eee;
}

.aim-pagination-button {
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.aim-pagination-button:hover:not(:disabled) {
  background: #f5f5f5;
}

.aim-pagination-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.aim-pagination-info {
  font-size: 0.9rem;
  color: #666;
}

.icon-picker-trigger {
  display: inline-block;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}

.icon-picker-trigger:hover {
  transform: scale(1.1);
}

.animated-icon {
  transition: color 0.3s ease-in-out;
  perspective: 1000px;
  display: inline-block;
}

.animated-icon i {
  display: inline-block;
  transform: rotateX(20deg) rotateY(20deg);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.animated-icon:hover i {
  transform: rotateX(0deg) rotateY(0deg) scale(1.1);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.2), 0 4px 6px rgba(0, 0, 0, 0.1);
}
</style>
